import { SidebarNavLink } from './sidebar-nav-link';
import { SidebarSection } from './sidebar-section';
import { SidebarToggleButton } from './sidebar-toggle-button';
import { SidebarHeader } from './sidebar-header';
import { useLocation } from 'react-router-dom';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { User, Coins, NotepadText, Award, Settings2, Rocket } from 'lucide-react';
import { useSidebar } from '@/shared/hooks/use-sidebar';
import { useSaleId } from '@/shared/hooks/use-sale-id';
import { paths } from '@/config/paths';
import { AuthContext } from '@/types/stores/auth';
import { MembershipRole } from '@/types/api/membership';
import { useAuth } from '@/hooks/auth/use-auth';
import { useGroupSuspense } from '@/features/groups/api/get-group';
import { useOnboardingProgress, withLocalProgress } from '@/features/groups/hooks/use-onboarding-progress';
import { GroupStatus } from '@/types/api/group';
import { OnboardingTaskStatus } from '@/types/api/group/onboarding-task';

export type NavLink = {
  path: string;
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const SALE_LOCKED_REASON = 'Tillgängligt när försäljningen har startat';

const getLinks = (saleId: string) => ({
  sections: [
    {
      label: 'Försäljning',
      lockedWhileOnboarding: true,
      links: [
        {
          path: paths.sale.overview.getHref(saleId),
          label: 'Översikt',
          Icon: Coins,
        },
        {
          path: paths.sale.participations.getHref(saleId),
          label: 'Säljare',
          Icon: Award,
        },
        {
          path: paths.sale.orders.getHref(saleId),
          label: 'Beställningar',
          Icon: NotepadText,
        },
      ],
    },
    {
      label: 'Hantera grupp',
      show: (context: AuthContext) => context.membershipRoles.includes(MembershipRole.Leader),
      links: [
        {
          path: paths.group.memberships.getHref(),
          label: 'Gruppmedlemmar',
          Icon: User,
        },
        {
          path: paths.group.settings.root.getHref(),
          label: 'Gruppinställningar',
          Icon: Settings2,
        },
      ],
    },
  ],
});

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (value: boolean) => void;
  variant?: string;
}

export function Sidebar({ sidebarOpen, setSidebarOpen }: SidebarProps) {
  const { trigger, sidebar, sidebarExpanded, setSidebarExpanded } = useSidebar(setSidebarOpen);
  const { pathname } = useLocation();
  const saleId = useSaleId();
  const links = getLinks(saleId ?? '_');
  const { context } = useAuth();
  const onboarding = useOnboardingStatus(context!.groupId!);

  // Close sidebar on route change
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [pathname, setSidebarOpen]);

  return (
    <div className="min-w-fit">
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={cn(
          `fixed inset-0 bg-gray-900 bg-opacity-30 z-40 
          lg:hidden lg:z-auto transition-opacity duration-200`,
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <div
        id="sidebar"
        ref={sidebar}
        className={cn(
          `flex lg:!flex flex-col absolute z-40 left-0 top-0
          lg:static lg:left-auto lg:top-auto lg:translate-x-0
          h-dvh overflow-y-scroll lg:overflow-y-auto no-scrollbar
          w-64 lg:w-20 lg:sidebar-expanded:!w-64 2xl:!w-64 shrink-0
          bg-white dark:bg-gray-800 p-4 transition-all duration-200 
          ease-in-out rounded-r-2xl shadow-sm`,
          sidebarOpen ? 'translate-x-0' : '-translate-x-64',
        )}
      >
        <SidebarHeader trigger={trigger} setSidebarOpen={setSidebarOpen} sidebarOpen={sidebarOpen} />

        {onboarding && (
          <ul className="mb-4">
            <SidebarNavLink
              path={paths.group.onboarding.getHref()}
              label="Kom igång"
              Icon={Rocket}
              badge={
                <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-xs font-medium text-violet-700 dark:text-violet-300">
                  {onboarding.completed}/{onboarding.total}
                </span>
              }
            />
          </ul>
        )}

        {links.sections
          .filter((s) => !s.show || s.show(context!))
          .map((section) => (
            <SidebarSection key={section.label} label={section.label}>
              {section.links.map((link) => (
                <SidebarNavLink
                  key={link.path}
                  path={link.path}
                  label={link.label}
                  Icon={link.Icon}
                  lockedReason={onboarding && 'lockedWhileOnboarding' in section ? SALE_LOCKED_REASON : undefined}
                />
              ))}
            </SidebarSection>
          ))}

        <SidebarToggleButton setSidebarExpanded={setSidebarExpanded} sidebarExpanded={sidebarExpanded} />
      </div>
    </div>
  );
}

/** The group's onboarding progress while it's onboarding, including steps just completed here */
function useOnboardingStatus(groupId: string) {
  const { data: group } = useGroupSuspense({ groupId });
  const tasks = withLocalProgress(group.onboardingTasks, useOnboardingProgress(groupId));
  const completed = tasks.filter((task) => task.status === OnboardingTaskStatus.Completed).length;

  if (group.status !== GroupStatus.Onboarding || completed === tasks.length) return null;

  return { completed, total: tasks.length };
}
